import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid } from '@mui/material';
import OrgSelectionStep from './OrgSelectionStep';
import {
  FormSubmitBar,
  FormValidationSummary,
  HookFormCheckbox,
  HookFormPasswordField,
  HookFormTextField,
} from '../../forms/components';
import useCognito from '../hooks/useCognito';
import useCurrentCloudUser from '@/shared/hooks/user/getCurrentUser';
import useUpdateSession from '@/shared/hooks/user/useUpdateSession';
import { Tracking } from '@/shared/services/tracking';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { Org } from '@/types/user';
import cognitoStorageAdapter from '../storage/cognitoStorageAdapter';
import { addOrUpdateAccount } from '../storage/knownAccountsRegistry';
import { getReturnUrl, returnPicksOwnOrg } from '../functions/returnUrl';

import { useSnackbar } from 'notistack';

type SignInFormValues = {
  username: string;
  password: string;
  rememberMe: boolean;
};

type SignInFormProps = {
  prefillEmail?: string;
  onBack?: () => void;
};

const validationSchema = Yup.object().shape({
  username: Yup.string().required().max(256).label('Username'),
  password: Yup.string().required().max(50).label('Password'),
  rememberMe: Yup.boolean().defined(),
});

const SignInForm = ({ prefillEmail, onBack }: SignInFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = getReturnUrl(location.search, window.location.origin);
  const skipOrgStep = returnPicksOwnOrg(returnUrl);

  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

  const [loggingIn, setLoggingIn] = useState(false);

  const cognito = useCognito();

  const snackBar = useSnackbar();

  const { data: { data } = {}, status, refetch: refetchUser, isLoading } = useCurrentCloudUser();
  const updateSession = useUpdateSession();

  const form = useForm<SignInFormValues>({
    defaultValues: {
      username: prefillEmail ?? '',
      password: '',
      rememberMe: cognitoStorageAdapter.getRememberMe(),
    },
    resolver: yupResolver(validationSchema),
  });

  useDocumentTitle('Log In');

  const currentUser = useMemo(() => data?.user, [data?.user]);

  useEffect(() => {
    if (status === 'error') {
      snackBar.enqueueSnackbar('Error fetching user data', { variant: 'error' });
    }
  }, [status]);

  // First effect: Fetch and set organizations when user data is available
  useEffect(() => {
    if (!cognito.authenticated || !currentUser) return;

    const availableOrgs = currentUser.orgs ?? [];
    setOrganizations(availableOrgs);
  }, [cognito.authenticated, currentUser]);

  // Second effect: Handle organization selection logic when organizations change
  useEffect(() => {
    if (!organizations.length) return;

    if (organizations.length === 0) {
      setGeneralErrors(['No organizations available for this user']);
      return;
    }

    if (organizations.length === 1) {
      // Auto-select the only available organization
      handleOrganizationSelect(organizations[0].id);
    } else if (skipOrgStep) {
      // The return page asks for the org itself (MCP consent): no org step, and no
      // PUT /session, which records the org as the user's own choice of default.
      addOrUpdateAccount({
        email: form.getValues('username').trim().toLowerCase(),
        cognitoUsername: cognito.user?.getUsername(),
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        orgName: data?.org?.name,
        remembered: form.getValues('rememberMe'),
        lastLoginAt: Date.now(),
      });
      Tracking.getInstance().signIn();
      navigate(returnUrl);
    } else {
      // For multiple orgs, wait for user selection
      setSelectedOrgId(null);
    }
  }, [organizations]);

  const handleOrganizationSelect = async (orgId: number) => {
    setLoggingIn(true);

    try {
      const org = organizations.find((o) => o.id === orgId);

      if (!org) {
        setGeneralErrors(['An error occurred during login. Please try again.']);
        setLoggingIn(false);
        return;
      }

      await updateSession.mutateAsync({ org: org.id });

      // Wait for the user cache to reflect the selected org before navigating.
      // navigate() changes isPublicRoute (true→false), which triggers SSE reconnect
      // and a 'initialized' event. If we navigate before GET /v1.0/user settles,
      // processRealtimeEvent reads the stale org ID and POST /session overwrites Redis.
      await refetchUser();

      // Record this account in the known accounts registry
      const email = form.getValues('username').trim().toLowerCase();
      const remembered = form.getValues('rememberMe');
      addOrUpdateAccount({
        email,
        cognitoUsername: cognito.user?.getUsername(),
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        orgName: org.name,
        remembered,
        lastLoginAt: Date.now(),
      });

      Tracking.getInstance().signIn();
      navigate(returnUrl);
    } catch (error) {
      setGeneralErrors(['Failed to select organization']);
      setLoggingIn(false);
    }
  };

  const handleSignIn = form.handleSubmit(async (values) => {
    setGeneralErrors([]); // Clear previous errors

    // Set storage preference before authenticating
    cognitoStorageAdapter.setRememberMe(values.rememberMe);

    try {
      await new Promise<void>((resolve, reject) => {
        cognito.authenticateUser({
          username: values.username.trim().toLowerCase(),
          password: values.password,
          onSuccess: () => {
            refetchUser();
            resolve();
          },
          onFailure: (message) => {
            setGeneralErrors([message]);
            reject(message);
          },
          newPasswordRequired: () => {
            Tracking.getInstance().track('Reset Password', { category: 'Auth' });
            // Keeps `returnUrl` across the detour; ResetPasswordForm hands it back.
            navigate(`/reset-password${location.search}`);
            resolve();
          },
        });
      });
    } catch (error) {
      // Error already handled in onFailure
    }
  });

  const handleContinueClick = () => {
    if (selectedOrgId === null) {
      setGeneralErrors(['Please select an organization']);
      return;
    }

    handleOrganizationSelect(selectedOrgId);
  };

  // Show org selection if authenticated and has multiple orgs
  if (cognito.authenticated && organizations.length > 1 && !skipOrgStep) {
    return (
      <OrgSelectionStep
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        loading={loggingIn}
        errors={generalErrors}
        onOrgChange={(id) => setSelectedOrgId(id)}
        onContinue={handleContinueClick}
        onLogOut={() =>
          cognito.signOut({
            onSuccess: () => navigate('/sign-in'),
            onFailure: () => navigate('/sign-in'),
          })
        }
      />
    );
  }

  // Show login form
  return (
    <FormProvider {...form}>
      <form noValidate style={{ width: '100%' }}>
        <FormValidationSummary validationErrors={generalErrors} />

        <HookFormTextField
          name="username"
          autoFocus={!prefillEmail}
          required
          label="Email"
          disabled={form.formState.isSubmitting || loggingIn || isLoading}
          variant="outlined"
          margin="dense"
        />

        <HookFormPasswordField
          name="password"
          required
          label="Password"
          autoFocus={!!prefillEmail}
          disabled={form.formState.isSubmitting || loggingIn || isLoading}
          variant="outlined"
          margin="dense"
          onEnter={handleSignIn}
        />

        <HookFormCheckbox
          name="rememberMe"
          label="Remember me"
          size="small"
          disabled={form.formState.isSubmitting || loggingIn || isLoading}
        />

        <FormSubmitBar
          submitting={form.formState.isSubmitting || loggingIn || isLoading}
          buttonText="Sign In"
          onSubmit={handleSignIn}
          mainButtonStyle={{ width: '50%' }}
        />

        <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <Button component={RouterLink} to="/forgot-password" sx={{ mt: 1, ml: -1, color: 'white' }}>
            Forgot password?
          </Button>
        </Grid>

        {onBack && (
          <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Button onClick={onBack} sx={{ color: 'white' }}>
              Back to accounts
            </Button>
          </Grid>
        )}
      </form>
    </FormProvider>
  );
};

export default SignInForm;
